import _ from 'lodash';
import { takeLatest, put, call, select } from 'redux-saga/effects';
import { checkToken } from '../auth/sagas';
import { Urls } from '../../config/urls';
import { getTask, postTask, getPDFTask, getCSVTask, getXlsxTask } from '../../services';
import {
  RefundsActionType,
  getRefundsSuccess,
  getRefundsError,
  getBanksSuccess,
  getBanksError,
  sendRequestRefundSuccess,
  sendRequestRefundError,
  getPaymentMethodsForRefundsSuccess,
  getPaymentMethodsForRefundsError,
  getRefundDetailSuccess,
  getRefundDetailError,
  exportRefundsSuccess,
  exportRefundsError,
} from './actions';
import { ExportType, PanelStore } from '../interfaces';

function* getRefunds(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  let params = `?page=${action.page || 0}`;

  const filters = action.filter;
  let filterParam = '';
  if (filters) {
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        filterParam += filterParam ? '&' : '';
        filterParam += `${_.snakeCase(key)}=${filters[key]}`;
      }
    });
  }

  params += filterParam ? `&${filterParam}` : '';

  const { response, error } = yield call(getTask, {
    url: Urls.api.allRefunds + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(getRefundsSuccess(response));
  } else {
    yield put(getRefundsError(error));
  }
}

export function* getRefundsSaga() {
  yield takeLatest(RefundsActionType.GET_REFUNDS, getRefunds);
}

function* getBanks(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: `${Urls.api.banks}?country=${action.country}`,
    token: state.auth.token,
  });

  if (response) {
    yield put(getBanksSuccess(response));
  } else {
    yield put(getBanksError(error));
  }
}

export function* getBanksSaga() {
  yield takeLatest(RefundsActionType.GET_BANKS, getBanks);
}

function* sendRequestRefund(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();
  const body = { ...action.values };
  const { response, error } = yield call(postTask, {
    url: Urls.api.requestRefunds,
    body,
    token: state.auth.token,
  });

  if (response) {
    yield put(sendRequestRefundSuccess(response));
  } else {
    yield put(sendRequestRefundError({ ...error, sentValues: body }));
  }
}

export function* sendRequestRefundSaga() {
  yield takeLatest(RefundsActionType.REQUEST_REFUND, sendRequestRefund);
}

function* getPaymentMethodsForRefunds(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: `${Urls.api.allPaymentMethods}?include_deleted=1&page_size=50&country=${action.country}`,
    token: state.auth.token,
  });

  if (response) {
    yield put(getPaymentMethodsForRefundsSuccess(response));
  } else {
    yield put(getPaymentMethodsForRefundsError(error));
  }
}

export function* getPaymentMethodsForRefundsSaga() {
  yield takeLatest(RefundsActionType.GET_PAYMENT_METHODS_FOR_REFUNDS, getPaymentMethodsForRefunds);
}

function* getRefundDetail(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: Urls.api.refundDetail.replace(':id', action.id),
    token: state.auth.token,
  });

  if (response) {
    yield put(getRefundDetailSuccess(response));
  } else {
    yield put(getRefundDetailError(error));
  }
}

export function* getRefundDetailSaga() {
  yield takeLatest(RefundsActionType.GET_REFUND_DETAIL, getRefundDetail);
}

function* exportRefundDetail(action: any): Generator<any, void, any> {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response } = yield call(getPDFTask, {
    url: Urls.api.refundDetailExportPDF.replace(':id', action.id),
    token: state.auth.token,
  });

  if (response) {
    const url = URL.createObjectURL(response);
    const a = document.createElement('a');
    a.href = url;
    a.download = `refund-${action.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export function* exportRefundDetailSaga() {
  yield takeLatest(RefundsActionType.EXPORT_REFUND_DETAIL, exportRefundDetail);
}

function* exportRefunds(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const params = action.filter ? `?${action.filter}` : '';
  const exportType: ExportType = action.exportType;

  const { task, url }: { task: typeof getCSVTask; url: string } = {
    [ExportType.CSV]: {
      task: getCSVTask,
      url: Urls.api.exportRefunds,
    },
    [ExportType.XLSX]: {
      task: getXlsxTask,
      url: Urls.api.exportRefundsExcel,
    },
  }[exportType];

  const { response, error } = yield call(task, {
    url: url + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(exportRefundsSuccess(response));
  } else {
    yield put(exportRefundsError(error));
  }
}

export function* exportRefundsSaga() {
  yield takeLatest(RefundsActionType.EXPORT_REFUNDS, exportRefunds);
}
